import { useRef } from 'react';
import { TabButton, TabStatus } from './TabButton';
import { TabsDropdown } from './TabsDropdown';
import styled from 'styled-components';
import { ButtonType, IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { noop } from 'lodash';
import { useChevronSliders, useResizeObserver } from '@ardoq/hooks';
import { colors, space } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';

export type Tab<IdType extends string | number> = {
  id: IdType;
  label: string;
  status?: TabStatus;
  onTabClear?: () => void;
};

type BaseTabsProps<IdType extends string | number> = {
  i18nLocale: I18nLocale;
  tabs: Tab<IdType>[];
  activeTabId?: IdType;
  onTabChange?: (tabId: IdType) => void;
  searchPlaceholder?: string;
  clearTabTooltipText?: string;
  onTabCreate?: () => void;
  scrollLeftLabel?: string;
  scrollRightLabel?: string;
  addTabLabel?: string;
  allTabsLabel?: string;
};

type EditableTabsProps<IdType extends string | number> =
  BaseTabsProps<IdType> & {
    editable: true;
  };

type NonEditableTabsProps<IdType extends string | number> =
  BaseTabsProps<IdType> & {
    editable?: false;
  };

type TabsProps<IdType extends string | number> =
  | EditableTabsProps<IdType>
  | NonEditableTabsProps<IdType>;

export const Tabs = <IdType extends string | number>(
  props: TabsProps<IdType>
) => {
  const {
    i18nLocale,
    activeTabId,
    onTabChange = noop,
    tabs = [],
    searchPlaceholder,
    editable,
    clearTabTooltipText,
    scrollLeftLabel = t(
      i18nLocale,
      'integrations:tabs.scrollLeft',
      'Scroll left'
    ),
    scrollRightLabel = t(
      i18nLocale,
      'integrations:tabs.scrollRight',
      'Scroll right'
    ),
    addTabLabel = t(i18nLocale, 'integrations:tabs.addTab', 'Add tab'),
    allTabsLabel,
  } = props;

  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const { width: tabsContainerWidth = 0 } = useResizeObserver(tabsContainerRef);

  const {
    wrapperRef,
    wrapperOnScroll,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick,
    onRightChevronClick,
  } = useChevronSliders(tabsContainerWidth, [tabs.length]);

  const hasOverflow = isLeftChevronVisible || isRightChevronVisible;

  const scrollToTab = (tabId: IdType) => {
    if (!wrapperRef.current) return;

    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    if (tabIndex === -1) return;

    const tabElement = wrapperRef.current.children[tabIndex];
    // Our test runner does not have scrollIntoView, removing this check will cause tests to fail
    if (!tabElement || typeof tabElement.scrollIntoView !== 'function') return;

    tabElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  const handleTabChange = (tabId: IdType) => {
    scrollToTab(tabId);
    onTabChange(tabId);
  };

  return (
    <Wrapper>
      <TabsContainer ref={tabsContainerRef}>
        {hasOverflow && (
          <ArrowLeft
            label={scrollLeftLabel}
            onClick={onLeftChevronClick}
            iconName={IconName.CHEVRON_LEFT}
          />
        )}
        <ScrollingSpace ref={wrapperRef} onScroll={wrapperOnScroll}>
          {tabs.map((tab, index) => {
            // The last remaining tab can not be cleared for editable tabs
            const onClear =
              !editable || tabs.length > 1 ? tab.onTabClear : undefined;
            return (
              <TabButton
                i18nLocale={i18nLocale}
                tabIndex={index + 1}
                key={tab.id}
                label={tab.label}
                isActive={tab.id === activeTabId}
                status={tab.status}
                onClick={() => handleTabChange(tab.id)}
                onClear={onClear}
                isLast={index === tabs.length - 1}
                editable={editable}
                clearTabTooltipText={clearTabTooltipText}
              />
            );
          })}
        </ScrollingSpace>
        {hasOverflow && (
          <ArrowRight
            label={scrollRightLabel}
            onClick={onRightChevronClick}
            iconName={IconName.CHEVRON_RIGHT}
          />
        )}
        {editable && props.onTabCreate && (
          <AddTabButton
            label={addTabLabel}
            onClick={props.onTabCreate}
            iconName={IconName.ADD}
          />
        )}
      </TabsContainer>
      {hasOverflow && (
        <TabsDropdown
          i18nLocale={i18nLocale}
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={handleTabChange}
          searchPlaceholder={searchPlaceholder}
          allTabsLabel={allTabsLabel}
        />
      )}
    </Wrapper>
  );
};

const ScrollingSpace = styled.div`
  overflow: auto;
  position: relative;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  display: flex;
`;

const Arrow = styled(IconButton).attrs({
  buttonType: ButtonType.GHOST,
})`
  height: 100%;
  border-radius: 0;
  padding: 0 ${space.small};
`;

const ArrowRight = styled(Arrow)`
  border-left: 1px solid ${colors.borderNeutral};
`;

const ArrowLeft = styled(Arrow)`
  border-right: 1px solid ${colors.borderNeutral};
`;

const AddTabButton = styled(Arrow)`
  border-left: 1px solid ${colors.borderNeutral};
`;

const Wrapper = styled(FlexBox).attrs({
  width: 'full',
})`
  box-shadow: ${colors.borderNeutral} 0px -1px 0px 0px inset;
`;

// Why can't we use FlexBox here?
// The ref is not working with FlexBox component, so we need to use a div
const TabsContainer = styled.div`
  display: flex;
  position: relative;
  overflow: hidden;
`;
